import React, { useEffect, useState } from 'react';
import './ErrorPage.scss';
import { useAuth } from '../../context/AuthContext';

// Enhanced ErrorPage Component with built-in runtime error handling
export const ErrorPage = ({ 
  isRuntimeError = false, 
  error = null,
  customMessage = null,
  showErrorDetails = false
}) => {
  const { logout } = useAuth();
  const [runtimeError, setRuntimeError] = useState(null);
  const [showingRuntimeError, setShowingRuntimeError] = useState(false);

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      console.error('Unhandled promise rejection:', event.reason);
      setRuntimeError({
        type: 'Promise Rejection',
        message: 'An unexpected error occurred in the application',
        details: event.reason
      });
      setShowingRuntimeError(true);
      event.preventDefault(); // Prevent the default browser error handling
    };

    // Handle JavaScript errors
    const handleError = (event) => {
      console.error('JavaScript error:', event.error);
      setRuntimeError({
        type: 'JavaScript Error',
        message: 'A runtime error occurred',
        details: event.error
      });
      setShowingRuntimeError(true);
      event.preventDefault(); // Prevent the default browser error handling
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  const handleGoBack = () => {
    if (showingRuntimeError) {
      // Clear runtime error and try to recover
      setShowingRuntimeError(false);
      setRuntimeError(null);
    } else {
      window.history.back();
      setTimeout(() => {
        window.location.reload();
      }, 200);
    }
  };

  const handleLogout = () => {
    try {
      logout();
      window.location.href = '/login'; // Update this path as needed
    } catch (error) {
      console.error('Logout failed:', error);
      window.location.href = '/login';
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  // Determine what error to show
  const currentError = showingRuntimeError ? runtimeError : (error || null);
  const isCurrentlyRuntimeError = showingRuntimeError || isRuntimeError;
  const currentMessage = customMessage || 
    (isCurrentlyRuntimeError ? 'The application encountered an unexpected error and needs to recover.' : 'Please try one of the options below to continue.');

  // Don't render the error page if no error is present
  if (!showingRuntimeError && !isRuntimeError && !error && !customMessage) {
    return null;
  }

  return (
    <div className="error-page">
      <div className="error-box">
        <div className="error-icon">⚠️</div>
        <h2>Oops, something went wrong!</h2>
        
        <p>{currentMessage}</p>
        
        {isCurrentlyRuntimeError && currentError && (
          <div className="error-type">
            <strong>Error Type:</strong> {currentError.type || 'Runtime Error'}
          </div>
        )}

        {(showErrorDetails || process.env.NODE_ENV === 'development') && currentError && currentError.details && (
          <details className="error-details">
            <summary>Error Details {process.env.NODE_ENV === 'development' ? '(Development Mode)' : ''}</summary>
            <pre>{currentError.details.toString()}</pre>
            {currentError.details.stack && (
              <pre className="stack-trace">{currentError.details.stack}</pre>
            )}
          </details>
        )}
        
        <div className="error-buttons">
          <button
            className="error-button primary"
            onClick={handleGoBack}
          >
            {showingRuntimeError ? 'Try Again' : 'Go Back & Reload'}
          </button>
          
          <button
            className="error-button secondary"
            onClick={handleReload}
          >
            Reload Page
          </button>
          
          <button
            className="error-button tertiary"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};